import {
  useEffect,
  useState,
} from 'react'
import {
  Check,
  Pencil,
  X,
} from 'lucide-react'
import {
  authService,
  getUserFacingApiErrorMessage,
  type AssistantCharacter,
} from '../services/api'

/** Server-side cap on the chosen name (ASSISTANT_NAME_MAX_LENGTH). */
const MAX_NAME_LENGTH = 60

interface AssistantNameEditorProps {
  character: AssistantCharacter
  onRenamed: (character: AssistantCharacter) => void
}

/**
 * The name the assistant goes by, editable in place (GET-14050).
 *
 * Renaming here changes what the assistant is called everywhere — how it
 * introduces itself, its email From line and signature, its contact card, how
 * it answers the phone — while the character keeps its personality, picture
 * and voice. Before this field existed the only "rename" a user could reach
 * was the character picker, whose "Create Custom" replaces the persona
 * outright; one user opened that picker twenty times looking for a name box.
 *
 * Deliberately Settings-only: onboarding picks a character and moves on, and a
 * name box there would read as a required step.
 */
export function AssistantNameEditor({
  character,
  onRenamed,
}: AssistantNameEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(character.display_name)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editing) setDraft(character.display_name)
  }, [character.display_name, editing])

  const isRenamed = character.display_name !== character.name

  const save = async (name: string | null) => {
    setSaving(true)
    setError(null)
    try {
      const updated = await authService.updateAssistantName(name)
      onRenamed(updated)
      setEditing(false)
    } catch (err) {
      setError(getUserFacingApiErrorMessage(
        err,
        "That didn't save, so the name is unchanged. Give it another go.",
      ))
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      setError('Give your assistant a name, or reset to the original one.')
      return
    }
    if (trimmed === character.display_name) {
      setEditing(false)
      return
    }
    void save(trimmed)
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Meet {character.display_name}
        </h1>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Rename your assistant"
          aria-label="Rename your assistant"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {isRenamed && (
          <span className="text-xs text-gray-400">
            originally {character.name}
          </span>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <label htmlFor="assistant-name" className="sr-only">
          Assistant name
        </label>
        <input
          id="assistant-name"
          type="text"
          value={draft}
          maxLength={MAX_NAME_LENGTH}
          autoFocus
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit()
            if (e.key === 'Escape') setEditing(false)
          }}
          className="text-2xl sm:text-3xl font-bold text-gray-900 border-b-2 border-primary-300 focus:border-primary-500 outline-none bg-transparent min-w-0 w-56 sm:w-72"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
          title="Save name"
          aria-label="Save name"
        >
          <Check className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Cancel"
          aria-label="Cancel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        The name your assistant goes by everywhere: chat, email, phone.
        Personality and voice stay the same.
        {isRenamed && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => void save(null)}
              disabled={saving}
              className="text-primary-600 hover:underline disabled:opacity-50"
            >
              Reset to {character.name}
            </button>
          </>
        )}
      </p>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
